/**
 * State：封装本模块的领域数据与相关操作。
 */
export class State {
    /**
     * name，保存当前实例的name状态。
     */
    name: string;
    /**
     * id，保存当前实例的id状态。
     */
    id: string | number;
    /**
     * totalRoundNum，保存当前实例的totalRoundNum状态。
     */
    totalRoundNum: number;
    /**
     * remainRoundNum，保存当前实例的remainRoundNum状态。
     */
    remainRoundNum: number;

    /**
     *
     * @param name 名称
     * @param id 序号，用以显示图标
     * @param roundNum 持续回合数
     */
    constructor(name: string, id: string | number, roundNum: number) {
        this.name = name;
        this.id = id;
        this.totalRoundNum = this.remainRoundNum = roundNum;
    }

    /** 获取异常状态图标的本地资源地址。 */
    getImgUrl() {
        return `/assets/state/${encodeURIComponent(this.name)}.svg`;
    }
}

/**
 * 异常免疫：封装本模块的领域数据与相关操作。
 */
export class 异常免疫 extends State {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum: number) {
        super("异常免疫", "img/异常免疫.png", roundNum);
    }

    /**
     * getImgUrl：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    getImgUrl(): string {
        return "";
    }
}

/**
 * AbnormalState：封装本模块的领域数据与相关操作。
 */
export class AbnormalState extends State {
    /**
     * randomDuration，保存当前实例的randomDuration状态。
     */
    readonly randomDuration: boolean;
    /**
     * 是否为限制类异常状态
     * 限制类异常状态：处于此类异常状态时精灵不能释放技能
     * 非限制类异常状态：处于此类异常状态时精灵可以释放技能，但可能会受到其他效果影响
     */
    isRestricted: boolean;

    /**
     * 创建实例并初始化所需的领域状态。
     * @param name 名称
     * @param id id参数，参与本次操作
     * @param isRestricted isRestricted参数，参与本次操作
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(name: string, id: string | number, isRestricted: boolean, roundNum?: number) {
        super(name, id, roundNum ?? 1);
        this.randomDuration = roundNum === undefined;
        this.isRestricted = isRestricted;
    }
}

/**
 * 中毒：封装本模块的领域数据与相关操作。
 */
export class 中毒 extends AbnormalState {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum?: number) {
        super("中毒", 1, false, roundNum);
    }
}

/**
 * 烧伤：封装本模块的领域数据与相关操作。
 */
export class 烧伤 extends AbnormalState {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum?: number) {
        super("烧伤", 2, false, roundNum);
    }
}

/**
 * 冻伤：封装本模块的领域数据与相关操作。
 */
export class 冻伤 extends AbnormalState {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum?: number) {
        super("冻伤", 5, false, roundNum);
    }
}

/**
 * 害怕：封装本模块的领域数据与相关操作。
 */
export class 害怕 extends AbnormalState {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum?: number) {
        super("害怕", 6, true, roundNum);
    }
}

/**
 * 疲惫：封装本模块的领域数据与相关操作。
 */
export class 疲惫 extends AbnormalState {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum?: number) {
        super("疲惫", 7, true, roundNum);
    }
}

/**
 * 睡眠：封装本模块的领域数据与相关操作。
 */
export class 睡眠 extends AbnormalState {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum?: number) {
        super("睡眠", 8, true, roundNum);
    }
}

/**
 * 麻痹：封装本模块的领域数据与相关操作。
 */
export class 麻痹 extends AbnormalState {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum?: number) {
        super("麻痹", 0, true, roundNum);
    }
}

/**
 * 石化：封装本模块的领域数据与相关操作。
 */
export class 石化 extends AbnormalState {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum?: number) {
        super("石化", 9, true, roundNum);
    }
}

/**
 * 冰封：封装本模块的领域数据与相关操作。
 */
export class 冰封 extends AbnormalState {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum?: number) {
        super("冰封", 15, true, roundNum);
    }
}

/**
 * 焚烬：封装本模块的领域数据与相关操作。
 */
export class 焚烬 extends AbnormalState {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum?: number) {
        super("焚烬", 22, true, roundNum);
    }
}

/**
 * 毒噬：封装本模块的领域数据与相关操作。
 */
export class 毒噬 extends AbnormalState {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param roundNum roundNum参数，参与本次操作
     */
    constructor(roundNum?: number) {
        super("毒噬", 27, true, roundNum);
    }
}
